import { useEffect, useMemo, useState } from 'react';
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useMergeUsersMutation, useMergeUsersPreviewMutation } from '@/hooks/users/useMergeUsers';
import { getRoleLabel } from '../utils/userBadges';
import { ProfileWithSubscription } from '@/types/auth/auth';
import { MergeConflictResolutions } from '@/types/users/merge';

interface MergeUsersDialogProps {
  users: ProfileWithSubscription[];
  isOpen: boolean;
  onClose: () => void;
  onMergeStarted: (jobId: number) => void;
}

type Step = 'primary' | 'conflicts' | 'preview';

export const MergeUsersDialog = ({ users, isOpen, onClose, onMergeStarted }: MergeUsersDialogProps) => {
  const [step, setStep] = useState<Step>('primary');
  const [primaryUserId, setPrimaryUserId] = useState<string>('');
  const [conflictResolutions, setConflictResolutions] = useState<MergeConflictResolutions>({});

  const previewMutation = useMergeUsersPreviewMutation();
  const mergeMutation = useMergeUsersMutation();

  useEffect(() => {
    if (isOpen) {
      setStep('primary');
      setPrimaryUserId(users[0]?.id || '');
      setConflictResolutions({});
      previewMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const minorUsers = useMemo(() => users.filter((u) => u.id !== primaryUserId), [users, primaryUserId]);
  const minorUserIds = useMemo(() => minorUsers.map((u) => u.id), [minorUsers]);

  const primaryUser = users.find((u) => u.id === primaryUserId);

  const roleValues = useMemo(() => Array.from(new Set(users.map((u) => u.role || ''))), [users]);
  const teamValues = useMemo(() => Array.from(new Set(users.map((u) => u.team_id ?? null))), [users]);

  const hasRoleConflict = roleValues.length > 1;
  const hasTeamConflict = teamValues.length > 1;
  const hasAnyConflict = hasRoleConflict || hasTeamConflict;

  const handleContinueFromPrimary = () => {
    setConflictResolutions({
      role: primaryUser?.role,
      team_id: primaryUser?.team_id ?? null,
    });
    setStep(hasAnyConflict ? 'conflicts' : 'preview');
    if (!hasAnyConflict) {
      previewMutation.mutate({ primaryUserId, minorUserIds });
    }
  };

  const handleContinueFromConflicts = () => {
    setStep('preview');
    previewMutation.mutate({ primaryUserId, minorUserIds });
  };

  const handleConfirmMerge = () => {
    mergeMutation.mutate(
      { primaryUserId, minorUserIds, conflictResolutions },
      {
        onSuccess: (data) => {
          onMergeStarted(data.jobId);
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-lg">
        <DialogHeader>
          <DialogTitle>Merge Accounts</DialogTitle>
        </DialogHeader>

        {step === 'primary' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Choose which account stays. All data from the other account(s) will be reassigned to it. The other
              accounts will be left empty — they are not deleted automatically.
            </p>
            <RadioGroup value={primaryUserId} onValueChange={setPrimaryUserId} className="space-y-2">
              {users.map((user) => (
                <label
                  key={user.id}
                  htmlFor={`primary-${user.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50"
                >
                  <RadioGroupItem value={user.id} id={`primary-${user.id}`} />
                  <div className="min-w-0">
                    <div className="font-medium text-sm"><CopyableEmail email={user.full_name || user.email || user.id} textClassName="truncate" /></div>
                    <div className="text-xs text-gray-500 truncate">
                      <CopyableEmail email={user.email} /> • {getRoleLabel(user.role ?? null)} •{' '}
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                    </div>
                  </div>
                </label>
              ))}
            </RadioGroup>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleContinueFromPrimary} disabled={!primaryUserId}>
                Continue
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'conflicts' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              These fields differ between the selected accounts. Choose which value the merged account should keep.
            </p>

            {hasRoleConflict && (
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select
                  value={conflictResolutions.role || ''}
                  onValueChange={(val) => setConflictResolutions((prev) => ({ ...prev, role: val }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roleValues.map((role) => (
                      <SelectItem key={role} value={role}>
                        {getRoleLabel(role || null)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {hasTeamConflict && (
              <div className="space-y-1.5">
                <Label>Team</Label>
                <Select
                  value={String(conflictResolutions.team_id ?? 'none')}
                  onValueChange={(val) =>
                    setConflictResolutions((prev) => ({ ...prev, team_id: val === 'none' ? null : Number(val) }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamValues.map((teamId) => (
                      <SelectItem key={String(teamId)} value={teamId === null ? 'none' : String(teamId)}>
                        {teamId === null ? 'No team' : `Team #${teamId}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('primary')}>
                Back
              </Button>
              <Button onClick={handleContinueFromConflicts}>Continue</Button>
            </DialogFooter>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-4">
            {previewMutation.isPending && <p className="text-sm text-gray-500">Loading preview…</p>}

            {previewMutation.data && (
              <>
                <p className="text-sm text-gray-600">
                  The following data will be reassigned to{' '}
                  <span className="font-medium text-gray-900">
                    <CopyableEmail email={primaryUser?.full_name || primaryUser?.email || primaryUserId} />
                  </span>
                  :
                </p>

                <div className="rounded-lg border p-3 bg-gray-50 space-y-1">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Minor account{minorUsers.length > 1 ? 's' : ''} (will be left empty)
                  </div>
                  {minorUsers.map((user) => (
                    <div key={user.id} className="text-sm text-gray-700 truncate">
                      {user.full_name || 'No name'}
                      {user.email && <span className="text-gray-500"> • <CopyableEmail email={user.email} /></span>}
                    </div>
                  ))}
                </div>

                <div className="max-h-56 overflow-y-auto rounded border divide-y">
                  {previewMutation.data.tableCounts.length === 0 && (
                    <div className="p-3 text-sm text-gray-400 italic">No data found on the selected accounts</div>
                  )}
                  {previewMutation.data.tableCounts.map((row) => (
                    <div key={row.table} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-gray-700">{row.table}</span>
                      <span className="font-medium text-gray-900">{row.count}</span>
                    </div>
                  ))}
                </div>

                {previewMutation.data.warnings.map((warning, i) => (
                  <Alert key={i} variant="destructive" className="bg-amber-50 border-amber-200 text-amber-800">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{warning}</AlertDescription>
                  </Alert>
                ))}
              </>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(hasAnyConflict ? 'conflicts' : 'primary')}>
                Back
              </Button>
              <Button
                onClick={handleConfirmMerge}
                disabled={mergeMutation.isPending || previewMutation.isPending || !previewMutation.data}
                className="bg-genie-primary hover:bg-genie-primary/90 text-white"
              >
                {mergeMutation.isPending ? 'Starting merge…' : 'Confirm Merge'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
